class VideoHeroCarousel extends HTMLElement {
  constructor() {
    super();
    this.slides = Array.from(this.querySelectorAll('[data-vhc-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-vhc-dot]'));
    this.interval = parseInt(this.dataset.interval, 10) || 6000;
    this.activeIndex = 0;
    this.autoplayEnabled = this.dataset.autoplay !== 'false';
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  connectedCallback() {
    if (this.slides.length === 0) return;

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goToSlide(index));
    });

    this.playSlide(0);

    if (this.autoplayEnabled && this.slides.length > 1 && !this.prefersReducedMotion) {
      this.startAutoplay();
    }

    this.sectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.resumeActiveVideo();
          } else {
            this.pauseAllVideos();
          }
        });
      },
      { threshold: 0.2 }
    );
    this.sectionObserver.observe(this);

    // Crossing the desktop/mobile breakpoint (resize, rotation) swaps which
    // video is visible via CSS - pause the one that just got hidden and
    // start the one that just became visible.
    this.mobileBreakpoint = window.matchMedia('(max-width: 749px)');
    this.handleBreakpointChange = () => {
      this.pauseAllVideos();
      this.resumeActiveVideo();
    };
    this.mobileBreakpoint.addEventListener('change', this.handleBreakpointChange);
  }

  disconnectedCallback() {
    this.stopAutoplay();
    if (this.sectionObserver) this.sectionObserver.disconnect();
    if (this.mobileBreakpoint) this.mobileBreakpoint.removeEventListener('change', this.handleBreakpointChange);
  }

  startAutoplay() {
    this.stopAutoplay();
    if (!this.autoplayEnabled) return;
    this.timer = window.setInterval(() => {
      this.goToSlide((this.activeIndex + 1) % this.slides.length);
    }, this.interval);
  }

  stopAutoplay() {
    if (this.timer) window.clearInterval(this.timer);
  }

  goToSlide(index) {
    if (index === this.activeIndex) return;
    this.playSlide(index);
    this.startAutoplay();
  }

  // A slide can hold both a desktop and a mobile video - only one is ever
  // visible (the CSS breakpoint hides the other), so pause/play must target
  // whichever one is actually on screen rather than always the first video.
  getVisibleVideo(slide) {
    const videos = slide.querySelectorAll('video');
    for (const video of videos) {
      if (window.getComputedStyle(video).display !== 'none') return video;
    }
    return null;
  }

  playSlide(index) {
    this.slides.forEach((slide, i) => {
      slide.classList.toggle('is-active', i === index);
      const video = this.getVisibleVideo(slide);
      if (!video) return;
      if (i === index) {
        video.currentTime = 0;
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });

    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === index);
    });

    this.activeIndex = index;
  }

  resumeActiveVideo() {
    const activeSlide = this.slides[this.activeIndex];
    const video = activeSlide ? this.getVisibleVideo(activeSlide) : null;
    if (video) video.play().catch(() => {});
  }

  pauseAllVideos() {
    this.slides.forEach((slide) => {
      const video = this.getVisibleVideo(slide);
      if (video) video.pause();
    });
  }
}

customElements.define('video-hero-carousel', VideoHeroCarousel);
